import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Eye, Users, Inbox, CalendarCheck, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-[#D4AF37]/40 transition-colors duration-300"
  >
    <div className="flex items-center justify-between">
      <p className="text-zinc-500 text-[10px] font-semibold tracking-[0.25em] uppercase">{label}</p>
      <Icon size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
    </div>
    <p className="mt-3 font-display text-white text-4xl font-black tracking-tight">{value}</p>
    {sub && <p className="mt-1 text-zinc-600 text-xs font-light">{sub}</p>}
  </div>
);

export default function AnalyticsTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/admin/analytics?days=${days}`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 py-16 justify-center">
        <Loader2 size={18} className="animate-spin text-[#D4AF37]" /> Loading analytics…
      </div>
    );
  }
  if (!data) return <p className="text-zinc-500 py-10">Could not load analytics.</p>;

  const maxViews = Math.max(1, ...data.by_day.map((d) => d.views));

  return (
    <div data-testid="analytics-content">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-white text-2xl font-bold uppercase tracking-tight">
          Last {days} days
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              axios
                .post(`${API}/admin/digest/send`, {}, { withCredentials: true })
                .then(() => toast.success("Digest email on its way to your inbox."))
                .catch(() => toast.error("Could not send digest."))
            }
            data-testid="send-digest-button"
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-[#D4AF37] text-white px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.12em] transition-colors duration-300"
          >
            <Mail size={13} /> Email Me This Summary
          </button>
          <div className="flex items-center border border-zinc-800">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`analytics-range-${d}`}
                className={`px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.12em] transition-colors ${
                  days === d ? "bg-[#D4AF37] text-zinc-950" : "text-zinc-400 hover:text-white"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Page Views" value={data.totals.views} testid="stat-views" />
        <StatCard icon={Users} label="Unique Visitors" value={data.totals.unique_visitors} testid="stat-visitors" />
        <StatCard
          icon={Inbox}
          label="Enquiries"
          value={data.totals.enquiries}
          sub={`${data.totals.enquiries_all_time} all time · ${data.totals.new_enquiries} unread`}
          testid="stat-enquiries"
        />
        <StatCard
          icon={CalendarCheck}
          label="Bookings"
          value={data.totals.bookings}
          sub={`${data.totals.bookings_all_time} all time`}
          testid="stat-bookings"
        />
      </div>

      {/* Views chart */}
      <div className="mt-8 bg-zinc-900/50 border border-zinc-800 p-6">
        <p className="text-zinc-500 text-[10px] font-semibold tracking-[0.25em] uppercase mb-6">
          Daily page views
        </p>
        <div className="flex items-end gap-[2px] h-40" data-testid="views-chart">
          {data.by_day.map((d) => (
            <div
              key={d.date}
              className="flex-1 group relative flex flex-col justify-end h-full"
              title={`${d.date}: ${d.views} views · ${d.visitors} visitors`}
            >
              <div
                className="w-full bg-[#D4AF37]/70 group-hover:bg-[#D4AF37] transition-colors min-h-[2px]"
                style={{ height: `${(d.views / maxViews) * 100}%` }}
              />
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between text-zinc-600 text-[10px] uppercase tracking-wider">
          <span>{data.by_day[0]?.date}</span>
          <span>{data.by_day[data.by_day.length - 1]?.date}</span>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top pages */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6" data-testid="top-pages">
          <p className="text-zinc-500 text-[10px] font-semibold tracking-[0.25em] uppercase mb-5">
            Most visited pages
          </p>
          {data.top_paths.length === 0 ? (
            <p className="text-zinc-600 text-sm font-light">No visits recorded yet — data starts collecting now.</p>
          ) : (
            <ul className="space-y-3">
              {data.top_paths.map((p) => (
                <li key={p.path} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300 font-mono">{p.path === "/" ? "/ (homepage)" : p.path}</span>
                  <span className="font-display text-[#D4AF37] font-bold">{p.views}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Leads per day */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6" data-testid="leads-chart">
          <p className="text-zinc-500 text-[10px] font-semibold tracking-[0.25em] uppercase mb-5">
            Enquiries &amp; bookings per day
          </p>
          <div className="flex items-end gap-[2px] h-28">
            {data.by_day.map((d) => {
              const total = d.enquiries + d.bookings;
              const max = Math.max(1, ...data.by_day.map((x) => x.enquiries + x.bookings));
              return (
                <div
                  key={d.date}
                  className="flex-1 flex flex-col justify-end h-full gap-[1px]"
                  title={`${d.date}: ${d.enquiries} enquiries, ${d.bookings} bookings`}
                >
                  <div className="w-full bg-white/60 min-h-0" style={{ height: `${(d.bookings / max) * 100}%` }} />
                  <div className="w-full bg-[#D4AF37]/80 min-h-0" style={{ height: `${(d.enquiries / max) * 100}%` }} />
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-5 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-2">
              <span className="h-2 w-2 bg-[#D4AF37]" /> Enquiries
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-2 w-2 bg-white/60" /> Bookings
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
